// Category Master — with Cost Center mapping (Feb-2026).
// Each category maps permanently to one Cost Center from Cost Center Master.
// This mapping powers bidirectional auto-fill on the Indent Sheet.

import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Plus, Edit, Trash2, Upload, Download, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import BulkDeleteBar from "@/components/BulkDeleteBar";

const inputCls = "border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
const Lbl = ({ children }) => (<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>);

export default function CategoryMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [ccList, setCcList] = useState([]);       // Cost Center Master
  const [edit, setEdit] = useState(null);         // {id?, name, cost_center}
  const [q, setQ] = useState("");
  const [ccFilter, setCcFilter] = useState("");   // "" = All | "__none__" = Unassigned | <cc name>
  const [selected, setSelected] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    const [cats, ccs] = await Promise.all([
      api.get("/categories"),
      api.get("/cost-centers").catch(() => ({ data: [] })),
    ]);
    setList(cats.data || []);
    setCcList((ccs.data || []).map((c) => c.name).filter(Boolean));
    setSelected([]);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (ids) =>
    setSelected((s) => (ids.every((id) => s.includes(id)) ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));

  const save = async () => {
    if (!edit.name?.trim()) return toast.error("Name required");
    try {
      const payload = {
        name: edit.name.trim(),
        cost_center: (edit.cost_center || "").trim(),
      };
      if (edit.id) await api.put(`/categories/${edit.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Deleted");
    load();
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}/categories/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "category_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/categories/import", fd);
    toast.success(`Imported ${data.inserted} entries`);
    load();
  };

  const filtered = list.filter((r) => {
    if (q.trim() && !r.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (ccFilter === "__none__") return !(r.cost_center || "").trim();
    if (ccFilter && ccFilter !== "__none__") return (r.cost_center || "").trim() === ccFilter;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Masters</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Category Master</h1>
          <p className="text-sm text-slate-500 mt-1">
            Categories used in the Item Master dropdown. Each category maps to a Cost Center — the mapping drives
            auto-fill between Category ↔ Cost Center on the Indent Sheet.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            data-testid="category-search"
            className={inputCls + " w-48"}
          />
          <select
            value={ccFilter}
            onChange={(e) => setCcFilter(e.target.value)}
            data-testid="category-cc-filter"
            className={inputCls + " w-56"}
            title="Filter by Cost Center"
          >
            <option value="">All Cost Centers</option>
            <option value="__none__">— Unassigned —</option>
            {ccList.map((cc) => (<option key={cc} value={cc}>{cc}</option>))}
          </select>
          <button onClick={downloadTemplate} data-testid="category-template-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Download size={14} /> Template</button>
          {canManage && (
            <>
              <input type="file" accept=".xlsx" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = ""; } }} />
              <button onClick={() => fileRef.current?.click()} data-testid="category-import-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Upload size={14} /> Import Excel</button>
              <button onClick={() => setEdit({ name: "", cost_center: "" })} data-testid="category-add-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold"><Plus size={14} /> Add</button>
            </>
          )}
        </div>
      </div>

      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Lbl>Name</Lbl>
            <input
              data-testid="category-name-input"
              autoFocus
              value={edit.name}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              className={inputCls}
              onKeyDown={(e) => e.key === "Enter" && save()}
            />
          </div>
          <div>
            <Lbl>Cost Center</Lbl>
            <select
              data-testid="category-cc-input"
              value={edit.cost_center || ""}
              onChange={(e) => setEdit({ ...edit, cost_center: e.target.value })}
              className={inputCls}
            >
              <option value="">— Unassigned —</option>
              {ccList.map((cc) => (<option key={cc} value={cc}>{cc}</option>))}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Auto-fills on Indent Sheet.</div>
          </div>
          <div className="col-span-1 md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setEdit(null)} className="border border-slate-300 px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><X size={14} /> Cancel</button>
            <button onClick={save} data-testid="category-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
          </div>
        </div>
      )}

      <BulkDeleteBar resource="categories" selectedIds={selected} onClear={() => setSelected([])} onDeleted={load} testid="category-bulk" />

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              {canManage && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    data-testid="category-select-all"
                    checked={filtered.length > 0 && filtered.every((r) => selected.includes(r.id))}
                    onChange={() => toggleAll(filtered.map((r) => r.id))}
                  />
                </th>
              )}
              <th className="text-left px-6 py-3 w-12">#</th>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3">Cost Center</th>
              {canManage && <th className="text-right px-6 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody data-testid="category-list">
            {filtered.length === 0 && (
              <tr><td colSpan={canManage ? 5 : 3} className="text-center py-12 text-slate-400">No entries</td></tr>
            )}
            {filtered.map((r, idx) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                {canManage && (
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(r.id)}
                      onChange={() => toggle(r.id)}
                      data-testid={`category-select-${r.id}`}
                    />
                  </td>
                )}
                <td className="px-6 py-3 font-mono text-xs">{idx + 1}</td>
                <td className="px-6 py-3 font-medium">{r.name}</td>
                <td className="px-6 py-3 text-xs">
                  {r.cost_center
                    ? <span className="inline-flex items-center gap-1 bg-sky-50 border border-sky-200 text-sky-800 rounded px-1.5 py-0.5 font-medium">{r.cost_center}</span>
                    : <span className="text-slate-300 italic">Unassigned</span>}
                </td>
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit({ id: r.id, name: r.name, cost_center: r.cost_center || "" })} className="p-1.5 hover:bg-slate-100 rounded text-slate-600 mr-1"><Edit size={14} /></button>
                    <button onClick={() => del(r.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
